import React from 'react';
import { Target, Eye, Award, Users, GraduationCap, Briefcase } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/landing/Footer';
import WhatsAppWidget from '../components/WhatsAppWidget';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { PROGRAMS } from '../data/programs';

const programList = (() => {
  const titles = PROGRAMS.map(p => p.title);
  return titles.length > 1
    ? `${titles.slice(0, -1).join(', ')}, and ${titles[titles.length - 1]}`
    : titles.join('');
})();

const VALUES = [
  { icon: Award, title: 'Quality', desc: '"Quality is not an act, it is a habit." We hold every batch, every module, and every mentor to the same high bar.' },
  { icon: Target, title: 'Commitment', desc: '"Commitment is what transforms a promise into reality." We stay invested in every learner until they land the outcome they came for.' },
  { icon: Briefcase, title: 'Placement', desc: '"The only way to do great work is to love what you do." We build curricula and interview prep around real hiring bars at real companies.' },
];

export default function AboutPage() {
  const storyRef = useScrollReveal('.about-reveal', { y: 32, stagger: 0.12 });
  const valuesRef = useScrollReveal('.value-reveal', { y: 32, stagger: 0.15 });

  return (
    <div data-testid="about-page">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-[104px] overflow-hidden bg-gradient-to-br from-[#0056D2] via-[#003E99] to-[#002B6B]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28 text-center">
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm text-white/90 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <GraduationCap className="w-4 h-4" />
            About A7 Globetech
          </div>
          <h1 className="font-heading font-black text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-5">
            Training People. Transforming Careers.
          </h1>
          <p className="font-body text-lg text-white/80 max-w-2xl mx-auto leading-relaxed">
            A7 Globetech is a software technologies training and development company helping
            professionals build in-demand skills and land real jobs — through expert-led
            courses, hands-on practice, and a structured path to placement.
          </p>
        </div>
      </section>

      {/* Story / Mission / Vision */}
      <section ref={storyRef} className="py-20 lg:py-28 bg-[#F7F9FC]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="about-reveal max-w-3xl mx-auto text-center mb-16">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-[#0F1F3D] mb-4">Who We Are</h2>
            <p className="font-body text-base text-[#5A6A85] leading-relaxed">
              We are a team of industry practitioners and certified trainers delivering
              hands-on training across six career-focused programs — {programList}.
              Every course is built and taught by professionals who bring 10+ years of
              real-world experience from top MNCs into the classroom, so learners practice
              the same tools and workflows used on the job, not just theory. Beyond training,
              our Interview Readiness Platform (IRP) puts every learner through 30 structured
              mock interview rounds with real consultants, so they walk into interviews
              prepared and confident.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="about-reveal bg-white rounded-2xl border border-[#E1E7EF] p-8">
              <div className="w-12 h-12 rounded-xl bg-[#0056D2]/10 flex items-center justify-center mb-4">
                <Target className="w-6 h-6 text-[#0056D2]" />
              </div>
              <h3 className="font-heading font-bold text-xl text-[#0F1F3D] mb-3">Our Mission</h3>
              <p className="font-body text-sm text-[#5A6A85] leading-relaxed">
                To equip every learner with industry-ready skills and the confidence to clear
                real interviews — closing the gap between classroom training and a genuine job
                offer.
              </p>
            </div>
            <div className="about-reveal bg-white rounded-2xl border border-[#E1E7EF] p-8">
              <div className="w-12 h-12 rounded-xl bg-[#0056D2]/10 flex items-center justify-center mb-4">
                <Eye className="w-6 h-6 text-[#0056D2]" />
              </div>
              <h3 className="font-heading font-bold text-xl text-[#0F1F3D] mb-3">Our Vision</h3>
              <p className="font-body text-sm text-[#5A6A85] leading-relaxed">
                To be the most trusted name in technology training — known for placement
                outcomes, not just certificates, across India and beyond.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values / Pillars */}
      <section ref={valuesRef} className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="value-reveal text-center mb-14">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-[#1F1F1F]">
              What Drives Us
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {VALUES.map(v => {
              const Icon = v.icon;
              return (
                <div key={v.title} className="value-reveal text-center bg-[#F7F9FC] rounded-2xl border border-[#E1E7EF] p-8">
                  <div className="w-14 h-14 rounded-full bg-[#0056D2]/10 flex items-center justify-center mb-4 mx-auto">
                    <Icon className="w-7 h-7 text-[#0056D2]" />
                  </div>
                  <h3 className="font-heading font-bold text-lg text-[#0F1F3D] mb-2">{v.title}</h3>
                  <p className="font-body text-sm text-[#5A6A85] leading-relaxed">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0D1B2A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <Users className="w-8 h-8 text-[#A8D4FF] mx-auto mb-4" />
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-3">
            Ready to start your transformation?
          </h2>
          <p className="font-body text-sm text-white/60 max-w-xl mx-auto mb-6">
            Explore our training programs and take the first step toward your next role.
          </p>
          <a
            href="/#services"
            className="inline-flex items-center gap-2 font-body font-semibold text-sm bg-white text-[#0056D2] px-7 py-3.5 rounded-md hover:bg-[#F0F6FF] transition-colors"
          >
            Explore Programs
          </a>
        </div>
      </section>

      <Footer />
      <WhatsAppWidget />
    </div>
  );
}
